import React from 'react';
import Feature from 'ol/Feature';
import * as olSphere from 'ol/sphere';
import { Geometry, Polygon as OlPolygon, Polygon } from 'ol/geom'
import ImageLayer from 'ol/layer/Image';
import ImageStatic from 'ol/source/ImageStatic';
import { ProgressSpinner } from 'primereact/progressspinner';
import Overlay from 'ol/Overlay';
import KML from 'ol/format/KML';

import { ModelService } from '@shared/models/model-service';
import { DrawInteractionTool } from '../tool-base';
import { Ajax2 } from '@shared/Ajax';
import { KmlExport } from '@openlayers/tools/KmlExport';
import { getArea, getLocalCoordinatesInCcwOrder } from '../tool-utils'
import { layerStore, Measurement, measurementStore } from './tool-volumes-store'
import { TreeNode } from '@shared/ui-native/Tree'
import { ReactUtils } from '@shared/ui-react/ReactUtils';


export interface CalculateVolumeParams {
    feature: Feature<Geometry>
    addMeasurement?: boolean
    updateMeasurement?: boolean
    customBaseHeight?: number
    customTolerance?: number
    id?: number | null
}

export interface DEMResponse {
    fill_volume: number
    cut_volume: number
    base_height: number
    heatmap_image: string
    dem_image: string
    tolerance: number
    color: object
    lowest_point: number
    highest_point: number
}

export interface ModelDEMResponse {
    fill_volume: number
    cut_volume: number
    base_height: number
    heatmap_image: string
    dem_image: string
    modelBase64Glb: string
    tolerance: number
    color: object
    lowest_point: number
    highest_point: number
}

export class VolumeTool extends DrawInteractionTool {
    onDrawStart(e) {
        const currFeature = e.feature
        const [evt, label] = this.bindEvents(currFeature)
        this.inProgressLabel = label
        this.geometryListener = evt
        document
            .querySelectorAll('.measure-label')
            .forEach((el) => (el.parentElement.style.pointerEvents = 'none'))
        super.onDrawStart(e)
    }

    async onDrawEnd(e) {
        const currFeature = e.feature
        await this.calculateVolume({ feature: currFeature, addMeasurement: true })
        super.onDrawEnd(e)
    }

    generateDrawingFromKml(kmlString: string) {
        const kmlFormat = new KML();
        const features = kmlFormat.readFeatures(kmlString, {
            featureProjection: this.mapWrapper.getProjection()
        });

        features.forEach(async (feature) => {
            if (feature.getGeometry().getType() === 'Polygon') {
                feature.set('type', 'volume-measurement');
                const label = this.createLabel(feature)
                feature.set('label', label)
                this.drawLayer.getSource().addFeature(feature);
                this.updateLabel(feature);

                this.bindEvents(feature);
                await this.calculateVolume({ feature: feature, addMeasurement: true });
            }
        });
    }

    calcArea(polygon) {
        let area
        if (kuura.getProjectProj4Code() === 'EPSG:4326') {
            area = Math.abs(olSphere.getArea(polygon))
        } else {
            const sourceProj = this.mapWrapper.getProjection()
            const ccwLocalCoordinates = getLocalCoordinatesInCcwOrder(
                polygon,
                sourceProj
            )
            area = getArea(ccwLocalCoordinates)
        }
        return area
    }

    getLabelText(feature, fillVolume = null, cutVolume = null, id = null) {
        const polygon = feature.getGeometry()
        const prefix = ''
        let volumeText = ''
        const area = this.calcArea(polygon)
        const error = feature.get('error')

        if (error) {
            volumeText = `<span style="color: red;">${error}</span>`;
        }
        else if (fillVolume || cutVolume || id) {
            volumeText = `${__('js_map_measuring_tools.measurement')}: ${id} <br> ${__('js_map_measuring_tools.fill')}: ${Math.round(fillVolume)} m<sup>3</sup><br> ${__('js_map_measuring_tools.cut')}: ${Math.round(cutVolume)} m<sup>3</sup>`
        }

        if (area > 1000000) {
            return `${volumeText} <br> ${__('js_map_measuring_tools.dem_area')}: ${prefix}${
                Math.round((area / 1000000) * 100) / 100
            } km<sup>2</sup>`
        }
        return `${volumeText} <br> ${__('js_map_measuring_tools.dem_area')}: ${prefix}${
            Math.round(area * 100) / 100
        } m<sup>2</sup>`
    }

    updateLabel(feature) {
        const geom = feature.getGeometry()
        const label = feature.get('label')
        label.getElement().innerHTML = this.getLabelText(feature)
        label.setPosition(geom.getInteriorPoint().getCoordinates())
    }

    async updateVolume(feature) {
        const measurementId = feature.get('measurementId')
        if (measurementId !== undefined) {
            const updateMeasurement = true
            await this.calculateVolume({ feature: feature, updateMeasurement })
        }
    }

    restoreFromCoords(coords: Coordinate[][][]) {
        if (!Array.isArray(coords)) return

        coords.forEach((measurement) => {
            const feature = new Feature({
                geometry: new OlPolygon(measurement),
            })
            feature.set('finished', true)
            this.drawLayer.getSource().addFeature(feature)
            this.bindEvents(feature)
            feature.getGeometry().dispatchEvent('change')
        })
    }

    async raiseError(feature, error) {
        feature.set('error', error)
        this.updateLabel(feature)
        this.removeLoadingIcon(feature)
    }

    async constructPayload(params: CalculateVolumeParams) {
        const { feature, customBaseHeight, customTolerance } = params;
        const targetLayer = layerStore.target;
        const referenceLayer = layerStore.reference;

        const formData = new FormData();
        let apiUrl = `${ModelService.getVizApiBaseUrl()}/calc-vol/`;

        // Determine target layer
        if (!targetLayer) {
            await this.raiseError(feature, __('js_map_measuring_tools.error_no_dem'));
            return;
        }
        formData.append('target_type', targetLayer instanceof TreeNode ? "model" : "dem");
        formData.append(
            targetLayer instanceof TreeNode ? 'target_model_uuid' : 'target_dem_url',
            targetLayer instanceof TreeNode ? targetLayer.uuid : targetLayer.demUrl
        );

        // Determine reference layer
        if (referenceLayer) {
            apiUrl += 'model-dem';
            if (referenceLayer instanceof TreeNode) {
                formData.append('reference_type', 'model')
                formData.append('reference_model_uuid', referenceLayer.uuid);
            } else {
                formData.append('reference_type', 'dem');
                formData.append('reference_dem_url', referenceLayer.demUrl);
            }
        } else {
            apiUrl += 'dem';
            if (customBaseHeight) formData.append('base_height', customBaseHeight.toString());
        }

        const geom = feature.getGeometry() as OlPolygon;
        formData.append('project_uuid', kuura.getProjectUuid());
        formData.append('area', this.calcArea(geom).toString());
        formData.append('file', KmlExport.getFile([feature]));
        if (customTolerance) formData.append('tolerance', customTolerance.toString());
        return { formData, apiUrl };
    }

    async updateElement(params: CalculateVolumeParams, response: DEMResponse | ModelDEMResponse) {
        const geom: Polygon = params.feature.getGeometry() as OlPolygon;
        const area: number = this.calcArea(geom);
        const label = params.feature.get('label')

        // Update label
        label.getElement().innerHTML = this.getLabelText(
            params.feature,
            response.fill_volume,
            response.cut_volume,
            params.id
        )
        label.setPosition(geom.getInteriorPoint().getCoordinates())
        this.applyHeatmap(params.feature, response.heatmap_image)
        this.removeLoadingIcon(params.feature)

        params.feature.set('measuredArea', area)
    }

    async handleMeasurement(params: CalculateVolumeParams, response: DEMResponse | ModelDEMResponse) {
        const measurement: Measurement = {
            fillVolume: response.fill_volume,
            cutVolume: response.cut_volume,
            base64Image: response.heatmap_image,
            demBase64Image: response.dem_image,
            modelBase64Glb: 'modelBase64Glb' in response ? response.modelBase64Glb : null,
            modelName: layerStore.target.text,
            baseHeight: response.base_height,
            feature: params.feature,
            tolerance: response.tolerance,
            color: response.color,
            highestPoint: response.highest_point,
            lowestPoint: response.lowest_point,
        }

        if (params.addMeasurement) {
            params.id = measurementStore.addMeasurement(measurement)
            params.feature.set('measurementId', params.id)
        }

        if (params.updateMeasurement) {
            const heatmapLayer = params.feature.get('heatmapLayer')
            if (heatmapLayer) {
                this.mapWrapper.getInstance().removeLayer(heatmapLayer)
                params.feature.set('heatmapLayer', null)
            }
            params.id = params.feature.get('measurementId')
            if (params.id !== undefined) {
                measurement.id = params.id
                measurementStore.updateMeasurement(measurement)
            }
        }

        await this.updateElement(
            params,
            response
        )
    }

    async calculateVolume(
        params: CalculateVolumeParams
    ): Promise<boolean> {

        // Add loading icon
        const overlay = params.feature.get('loadingOverlay')
        if (!overlay) {
            this.addLoadingIcon(params.feature)
        }

        // Calculate volume
        const { formData, apiUrl } = await this.constructPayload(params);
        let response: DEMResponse | ModelDEMResponse;
        try {
            response = await Ajax2({
                timeout: 30000,
                url: apiUrl,
                method: 'POST',
                data: formData,
                useToken: true,
                contentType: 'multipart/form-data',
            });
        } catch (error) {
            await this.raiseError(params.feature, __('js_map_measuring_tools.http_error'))
            return false;
        }

        // Update measurement
        await this.handleMeasurement(params, response);

        return true;
    }

    applyHeatmap = (feature, base64Image) => {
        const image = new Image()
        image.src = `data:image/webp;base64,${base64Image}`
        image.onload = () => {
            const geom = feature.getGeometry()
            const extent = geom.getExtent()
            const imageLayer = new ImageLayer({
                source: new ImageStatic({
                    url: image.src,
                    imageExtent: extent,
                    crossOrigin: 'anonymous'
                }),
            })
            imageLayer.setZIndex(9999)
            this.mapWrapper.getInstance().addLayer(imageLayer)
            feature.set('heatmapLayer', imageLayer)
        }
    }

    addLoadingIcon(feature) {
        const geom = feature.getGeometry()
        const coordinates = geom.getInteriorPoint().getCoordinates()
        const overlayElement = document.createElement('div')
        overlayElement.className = 'loading-icon'
        const spinner = document.createElement('div')
        ReactUtils.safeRender(<ProgressSpinner />, spinner)
        overlayElement.appendChild(spinner)

        const overlay = new Overlay({
            element: overlayElement,
            positioning: 'center-center',
            stopEvent: false,
            offset: [0, -15],
        })

        overlay.setPosition(coordinates)
        this.mapWrapper.getInstance().addOverlay(overlay)

        feature.set('loadingOverlay', overlay)
    }

    removeLoadingIcon(feature) {
        const overlay = feature.get('loadingOverlay')
        if (overlay) {
            this.mapWrapper.getInstance().removeOverlay(overlay)
            feature.set('loadingOverlay', null)
        }
    }

    removeAllLoadingIcons = () => {
        const features = this.drawLayer.getSource().getFeatures()
        features.forEach((feature) => {
            this.removeLoadingIcon(feature)
        })
    }

    clearMeasurement = (feature) => {
        const heatmapLayer = feature.get('heatmapLayer')
        if (heatmapLayer) {
            this.mapWrapper.getInstance().removeLayer(heatmapLayer)
            feature.set('heatmapLayer', null)
        }
        this.mapWrapper.getInstance().removeOverlay(feature.get('label'))
        this.drawLayer.getSource().removeFeature(feature)
        if (this.onDrawRemove) {
            this.onDrawRemove(feature)
        }
        const measurementId = feature.get('measurementId')
        measurementStore.deleteMeasurement(measurementId)
    }

    clearAll = () => {
        const features = this.drawLayer.getSource().getFeatures()
        features.forEach((feature) => {
            this.clearMeasurement(feature)
        })

        measurementStore.clearMeasurements()
        this.removeAllLoadingIcons()
        this.drawLayer.getSource().clear()
    }

    lastClickCoords = []
    bindEvents = (feature) => {
        const label = this.createLabel(feature)
        this.mapWrapper.getInstance().addOverlay(label)
        let changeTimeout
        const evt = feature.getGeometry().on('change', (e) => {
            clearTimeout(changeTimeout)
            const geom = feature.getGeometry()
            const coords = geom.getCoordinates()
            if (this.lastClickCoords.length !== coords[0].length) {
                this.onDrawProgressCallback(feature)
            }
            this.lastClickCoords = coords[0]
            this.updateLabel(feature)

            changeTimeout = setTimeout(() => {
                // Wait for 1 second before fetching new volume
                const calculatedArea = feature.get('measuredArea')
                const currArea = this.calcArea(geom)
                if (calculatedArea !== currArea) {
                    this.updateVolume(feature)
                }
            }, 1000)
        })

        feature.set('type', 'volume-measurement')
        feature.set('label', label)

        return [evt, label]
    }
}
